/**
 * smartQuery.js — Smart Omnibar Syntax Parser for Home Expenses.
 *
 * Supports direct plain text (treated as title search) as well as structured keys in any order:
 *  - addedby:deva | by:deva | paidby:deva
 *  - title:milk | "mustard oil"
 *  - date:26-aug-2026 | date:today | date:yesterday | date:2026-08-26
 *  - brand:fortune
 *  - cat:rashan_ghar | category:food
 *  - notes:monthly | desc:ration
 */

const MONTH_NAMES = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const padZero = (n) => String(n).padStart(2, "0");

/**
 * Parses user date input into standard YYYY-MM-DD format.
 * Supports:
 * - "today", "yesterday"
 * - "26-aug-2026", "26-Aug-2026", "26 aug 2026"
 * - "2026-08-26"
 * - "26-08-2026", "26/08/2026"
 */
export const parseDateInput = (str) => {
  if (!str) return null;
  const clean = str.trim().toLowerCase();
  const today = new Date();
  const toYMD = (d) =>
    `${d.getFullYear()}-${padZero(d.getMonth() + 1)}-${padZero(d.getDate())}`;

  if (clean === "today") return toYMD(today);
  if (clean === "yesterday") {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    return toYMD(y);
  }

  // YYYY-MM-DD
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(clean)) {
    const [y, m, d] = clean.split("-");
    return `${y}-${padZero(m)}-${padZero(d)}`;
  }

  // DD-MM-YYYY or DD/MM/YYYY
  const numMatch = clean.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (numMatch) {
    const [, d, m, y] = numMatch;
    return `${y}-${padZero(m)}-${padZero(d)}`;
  }

  // DD-Mon-YYYY (e.g. 26-aug-2026 or 26 aug 2026)
  const textMatch = clean.match(/^(\d{1,2})[-/\s]([a-z]{3,9})[-/\s](\d{4})$/);
  if (textMatch) {
    const [, d, mon, y] = textMatch;
    const mNum = MONTH_NAMES[mon.slice(0, 3)];
    if (mNum) return `${y}-${padZero(mNum)}-${padZero(d)}`;
  }

  return null;
};

/**
 * Parses a smart query string into structured column criteria.
 * Any free text without key: is treated as title search.
 *
 * Example:
 *  "addedby:deva date:26-aug-2026 title:milk"
 *  -> { title: "milk", paid_by: "deva", date: "2026-08-26", brand: "", ... }
 *
 *  "oil addedby:deva"
 *  -> { title: "oil", paid_by: "deva", date: "", ... }
 */
export const parseSmartQuery = (rawQuery) => {
  if (!rawQuery || !rawQuery.trim()) {
    return {
      title: "",
      paid_by: "",
      brand: "",
      expense_detail: "",
      category: "",
      date: "",
      hasKeys: false,
    };
  }

  // Regex matches key:"value with spaces" or key:value or "quoted text" or word
  const regex = /(?:(\w+):(?:"([^"]+)"|(\S+)))|(?:"([^"]+)"|(\S+))/g;
  let match;

  const result = {
    title: "",
    paid_by: "",
    brand: "",
    expense_detail: "",
    category: "",
    date: "",
    hasKeys: false,
  };
  const plainWords = [];

  while ((match = regex.exec(rawQuery)) !== null) {
    const key = match[1]?.toLowerCase();
    const quotedVal = match[2];
    const plainVal = match[3];
    const freeQuoted = match[4];
    const freeWord = match[5];

    const val = quotedVal || plainVal;

    if (key && val) {
      result.hasKeys = true;
      if (key === "title") {
        result.title = val;
      } else if (key === "addedby" || key === "paidby" || key === "by") {
        result.paid_by = val;
      } else if (key === "brand" || key === "store") {
        result.brand = val;
      } else if (key === "desc" || key === "notes" || key === "detail") {
        result.expense_detail = val;
      } else if (key === "cat" || key === "category") {
        result.category = val;
      } else if (key === "date" || key === "on" || key === "day") {
        const parsedDate = parseDateInput(val);
        result.date = parsedDate || val;
      }
    } else {
      const free = freeQuoted || freeWord;
      if (free) plainWords.push(free);
    }
  }

  if (plainWords.length > 0) {
    const freeText = plainWords.join(" ");
    result.title = result.title ? `${result.title} ${freeText}` : freeText;
  }

  return result;
};

/**
 * Combines existing filterModel.items with parsed smart query filters.
 */
export const mergeSmartQueryWithFilters = (rawQuery, existingItems = []) => {
  if (!rawQuery || !rawQuery.trim()) return existingItems;

  const parsed = parseSmartQuery(rawQuery);
  const items = [...existingItems];

  // Helper to upsert filter
  const upsert = (field, operator, value) => {
    if (!value) return;
    const idx = items.findIndex((i) => i.field === field);
    if (idx >= 0) {
      items[idx] = { field, operator, value };
    } else {
      items.push({ field, operator, value });
    }
  };

  if (parsed.title) upsert("title", "contains", parsed.title);
  if (parsed.paid_by) upsert("paid_by", "contains", parsed.paid_by);
  if (parsed.brand) upsert("brand", "contains", parsed.brand);
  if (parsed.expense_detail) upsert("expense_detail", "contains", parsed.expense_detail);
  if (parsed.category) upsert("category", "=", parsed.category);
  if (parsed.date) {
    // If date is set, replace any previous date filters with exact day match
    const filtered = items.filter((i) => i.field !== "expense_time");
    filtered.push({ field: "expense_time", operator: "=", value: parsed.date });
    return filtered;
  }

  return items;
};
